import { useCallback, useEffect, useRef, useState } from "react";

import {
  useFollowUserMutation,
  useUnfollowUserMutation,
} from "../app/services/follows";

interface UseFollowToggleResult {
  isFollowing: boolean;
  toggle: () => Promise<void>;
  isLoading: boolean;
}

/**
 * フォロー / アンフォローのトグルを扱う共通フック。
 *
 * - 初期値はマウント時の `initialIsFollowing` を使用する。
 * - 楽観的更新でアイコン状態を即座に反転し、API 成功時はレスポンスの
 *   `is_following` で確定値に同期、失敗時は元に戻す。
 * - 「最後にこのインスタンスが確定した値」を `lastConfirmedRef` に保持し、
 *   親から再注入される `initialIsFollowing` が **自分の確定値と異なるとき**
 *   だけ state を上書きする。
 *     - 自分の楽観更新ループでフリッカーしない
 *     - 別画面・別コンポーネントで follow / unfollow されてサーバ側が変わり、
 *       memberships 等の `is_following` が再取得されて props に流れ込んだ場合は
 *       追従する（例: オファー作成ウィザードの送信先選択カードでフォロー解除
 *       したとき、開きっぱなしの UserList の MemberFollowButton も同期する）
 */
export const useFollowToggle = (
  userId: number | undefined,
  initialIsFollowing: boolean,
): UseFollowToggleResult => {
  const [isFollowing, setIsFollowing] = useState<boolean>(initialIsFollowing);
  const [followUser, { isLoading: isPostingFollow }] = useFollowUserMutation();
  const [unfollowUser, { isLoading: isPostingUnfollow }] = useUnfollowUserMutation();

  /**
   * このフックインスタンスが「最後に確定済みとして扱った値」。
   *  - 初期値: 親から渡された `initialIsFollowing`
   *  - toggle 成功時: サーバ確定値（無ければ楽観更新の next）
   *  - toggle 失敗時: ロールバック値
   * 親 props が `lastConfirmedRef.current` と異なる場合のみ「外部要因で変わった」
   * と判定して反映する。
   */
  const lastConfirmedRef = useRef<boolean>(initialIsFollowing);

  useEffect(() => {
    if (initialIsFollowing === lastConfirmedRef.current) return;
    lastConfirmedRef.current = initialIsFollowing;
    setIsFollowing(initialIsFollowing);
  }, [initialIsFollowing]);

  const toggle = useCallback(async () => {
    if (!userId) return;
    const next = !isFollowing;
    setIsFollowing(next);
    try {
      const res = next
        ? await followUser(userId).unwrap()
        : await unfollowUser(userId).unwrap();
      const serverValue = res?.data?.is_following;
      const finalValue =
        typeof serverValue === "boolean" ? serverValue : next;
      lastConfirmedRef.current = finalValue;
      setIsFollowing(finalValue);
    } catch (err) {
      console.error("フォロー操作に失敗:", err);
      lastConfirmedRef.current = !next;
      setIsFollowing(!next);
    }
  }, [userId, isFollowing, followUser, unfollowUser]);

  return {
    isFollowing,
    toggle,
    isLoading: isPostingFollow || isPostingUnfollow,
  };
};
